import React from "react";
import { useTranslation } from "react-i18next";
import {
    StyledModal,
    StyledH2,
    StyledLabel,
    CheckboxIcon,
} from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { mutate } from "swr";
import { useSnapshot } from "valtio";

import { store } from "@components/VerticalLayout/store";
import { setGlobalDataIsUpdated } from "./store";
import { store as appStore } from "./Kiosk/store";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import { useFetchGlobal } from "../../api/hooks";

type ConfirmationDissociateType = {
    isModalOpened: boolean;
    setIsModalOpened: Function;
    selectedRows: any;
};

export function ConfirmationDissociate({
    isModalOpened,
    setIsModalOpened,
    selectedRows,
}: ConfirmationDissociateType) {
    const { t } = useTranslation();
    const { fetchGlobal } = useFetchGlobal();
    const { userID, franchiseID, shopID, operatorID, oneShop } = useSnapshot(
        store
    );
    const { displayMode } = useSnapshot(appStore);
    const uuidUser = localStorage.getItem("uuidUser");

    async function handleDissociatePeripheral() {
        const dataByType: Record<string, any[]> = {
            cash_management: [],
            printer: [],
            monetic: [],
        };
        let shopIds: any = [];

        for (let element of selectedRows) {
            if (element.peripheralType !== undefined) {
                const peripheralType = element.peripheralType;
                const typeData = dataByType[peripheralType];

                if (typeData) {
                    let indexInData = typeData.findIndex(
                        (el: any) => el.shopId === element.shopId
                    );
                    if (indexInData === -1) {
                        let data: any = {
                            userId: userID,
                            shopId: oneShop ? shopID : element.shopId,
                            franchiseId: franchiseID,
                            appId: "KIOSK",
                            ids: { [element.appId]: [element.peripheralId] },
                            appId_children: [element.appId],
                            peripheralName: {
                                [element.appId]: [element[t("Peripheral")]],
                            },
                            operatorId: operatorID,
                            uuidUser: uuidUser,
                            isArchived: true,
                            applicationName: [element.appName],
                        };
                        typeData.push(data);
                    } else {
                        typeData[indexInData] = {
                            ...typeData[indexInData],
                            ids: {
                                ...typeData[indexInData].ids,
                                [element.appId]: [
                                    ...(typeData[indexInData].ids[
                                        element.appId
                                    ] || []),
                                    element.peripheralId,
                                ],
                            },
                            peripheralName: {
                                ...typeData[indexInData].peripheralName,
                                [element.appId]: [
                                    ...(typeData[indexInData].peripheralName?.[
                                        element.appId
                                    ] || []),
                                    element[t("Peripheral")],
                                ],
                            },
                            appId_children: typeData[
                                indexInData
                            ].appId_children?.includes(element.appId)
                                ? typeData[indexInData].appId_children
                                : [
                                      ...(typeData[indexInData]
                                          .appId_children || []),
                                      element.appId,
                                  ],
                            applicationName: typeData[indexInData].applicationName?.includes(element.appName)
                                ? typeData[indexInData].applicationName
                                : [
                                      ...(typeData[indexInData].applicationName || []),
                                      element.appName,
                                  ],
                        };
                    }
                }

                if (!shopIds.includes(element.shopId))
                    shopIds.push(element.shopId);
            }
        }

        try {
            const apiURL = `${process.env.REACT_APP_API_V2_URL}/settings/application/peripheral`;
            const payload = {
                cash_management: dataByType.cash_management,
                printer: dataByType.printer,
                monetic: dataByType.monetic,
            };

            let result: any = await fetchGlobal(apiURL, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    authorization: `Bareer ${localStorage.getItem("jwt")}`,
                },
                body: JSON.stringify(payload),
            });
            if (!result.error) {
                toast.success(
                    `${t("The dissociation was successfully completed.")}`,
                    {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        icon: (
                            <CheckboxIcon height={25} width={25} fill="white" />
                        ),
                    }
                );
                RedisPublish(shopIds);
                setGlobalDataIsUpdated(true);
            }
        } catch (e) {
            toast.error(`${t("There's an error")}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }
    async function RedisPublish(shopIds: any) {
        let apiUrl = `${process.env.REACT_APP_API_V2_URL}/settings/peripheral/redis`;
        if (!oneShop) {
            for (let shopId of shopIds) {
                try {
                    const data: any = await fetchGlobal(apiUrl, {
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            shopId: shopId,
                        }),
                        method: "POST",
                    });
                    if (data.error) {
                        throw Error("Error while saving selection!");
                    }
                } catch (e: any) {
                    console.log(`${t("There's an error")}!`);
                }
            }
        } else {
            try {
                const data: any = await fetchGlobal(apiUrl, {
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        shopId: shopID,
                    }),
                    method: "POST",
                });
                if (data.error) {
                    throw Error("Error while saving selection!");
                }
                mutate(apiUrl, data);
            } catch (e: any) {
                console.log(`${t("There's an error")}!`);
            }
        }
    }

    async function handleDissociateApplication() {
        const dataByType: Record<string, any[]> = {
            cash_management: [],
            printer: [],
            monetic: [],
        };
        let shopIds: any = [];

        for (let element of selectedRows) {
            if (element.subRows === undefined && element.peripheralType !== undefined) {
                const peripheralType = element.peripheralType;
                const typeData = dataByType[peripheralType];

                if (typeData) {
                    let indexInData = typeData.findIndex(
                        (el: any) => el.shopId === element.shopId
                    );
                    if (indexInData === -1) {
                        let data: any = {
                            userId: userID,
                            shopId: oneShop ? shopID : element.shopId,
                            franchiseId: franchiseID,
                            appId: "KIOSK",
                            ids: { [element.appId]: [element.peripheralId] },
                            appId_children: [element.appId],
                            peripheralName: {
                                [element.appId]: [element.peripheral],
                            },
                            operatorId: operatorID,
                            uuidUser: uuidUser,
                            isArchived: true,
                            applicationName: [element.appName],
                        };
                        typeData.push(data);
                    } else {
                        typeData[indexInData] = {
                            ...typeData[indexInData],
                            ids: {
                                ...typeData[indexInData].ids,
                                [element.appId]: [
                                    ...(typeData[indexInData].ids[
                                        element.appId
                                    ] || []),
                                    element.peripheralId,
                                ],
                            },
                            peripheralName: {
                                ...typeData[indexInData].peripheralName,
                                [element.appId]: [
                                    ...(typeData[indexInData].peripheralName?.[
                                        element.appId
                                    ] || []),
                                    element.peripheral,
                                ],
                            },
                            appId_children: typeData[
                                indexInData
                            ].appId_children?.includes(element.appId)
                                ? typeData[indexInData].appId_children
                                : [
                                      ...(typeData[indexInData]
                                          .appId_children || []),
                                      element.appId,
                                  ],
                            applicationName: typeData[indexInData].applicationName?.includes(element.appName)
                                ? typeData[indexInData].applicationName
                                : [
                                      ...(typeData[indexInData].applicationName || []),
                                      element.appName,
                                  ],
                        };
                    }
                }

                if (!shopIds.includes(element.shopId))
                    shopIds.push(element.shopId);
            }
        }

        try {
            const apiURL = `${process.env.REACT_APP_API_V2_URL}/settings/application/peripheral`;
            const payload = {
                cash_management: dataByType.cash_management,
                printer: dataByType.printer,
                monetic: dataByType.monetic,
            };

            let result: any = await fetchGlobal(apiURL, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    authorization: `Bareer ${localStorage.getItem("jwt")}`,
                },
                body: JSON.stringify(payload),
            });
            if (!result.error) {
                toast.success(
                    `${t("The dissociation was successfully completed.")}`,
                    {
                        position: toast.POSITION.TOP_CENTER,
                        autoClose: 2000,
                        theme: "colored",
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        icon: (
                            <CheckboxIcon height={25} width={25} fill="white" />
                        ),
                    }
                );
                RedisPublish(shopIds);
                setGlobalDataIsUpdated(true);
            }
        } catch (e) {
            toast.error(`${t("There's an error")}`, {
                position: toast.POSITION.TOP_CENTER,
                autoClose: 2000,
                theme: "colored",
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
        }
    }

    return (
        <StyledModal toggle={true} isOpen={isModalOpened!} centered>
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsModalOpened(!isModalOpened);
                }}
            >
                <StyledH2 className="text-uppercase">{t("Alert")}</StyledH2>
            </ModalHeader>
            <ModalBody>
                <StyledLabel className="pl-3 m-0">
                    {displayMode !== t("View by device")
                        ? t("Please confirm unpairing of the selected devices.")
                        : t("Please confirm unpairing of the application")}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0">
                <CustomSecondaryColorButton
                    outline
                    variant="light"
                    className="afa_btn_cancelAlert"
                    onClick={() => {
                        setIsModalOpened(!isModalOpened);
                    }}
                    rounded
                >
                    {t("Cancel")}
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    className="mr-2 afa_btn_confirmAlert"
                    variant="primary"
                    onClick={async () => {
                        setIsModalOpened(!isModalOpened);
                        displayMode !== t("View by device")
                            ? await handleDissociatePeripheral()
                            : await handleDissociateApplication();
                    }}
                >
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
        </StyledModal>
    );
}
